import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { tx, useLang } from "../../lib/i18n";

// Back to the previous in-app page, or a safe default when opened directly
export const BackButton = ({ fallback, label = "Back", onBack, testid = "back-button" }) => {
  useLang();
  const nav = useNavigate();
  const go = () => (window.history.state?.idx > 0 ? nav(-1) : nav(fallback));
  return (
    <button type="button" onClick={() => (onBack ? onBack(go) : go())} data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--mv-border-2)] px-3 py-1.5 text-xs text-[var(--mv-muted)] transition-colors hover:border-[var(--mv-teal)] hover:text-[var(--mv-teal)]">
      <ArrowLeft className="h-3.5 w-3.5" />{tx(label)}
    </button>
  );
};

// Protects unsaved form data: tab close/refresh, in-app links, and explicit Back/Cancel via ask(next)
export const useUnsavedGuard = (dirty, onSave) => {
  const [pending, setPending] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    const before = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    const click = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || !dirtyRef.current || a.target === "_blank" || e.metaKey || e.ctrlKey) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname + url.search === loc.pathname + loc.search) return;
      e.preventDefault(); e.stopPropagation();
      setPending(() => () => nav(url.pathname + url.search));
    };
    window.addEventListener("beforeunload", before);
    document.addEventListener("click", click, true);
    return () => { window.removeEventListener("beforeunload", before); document.removeEventListener("click", click, true); };
  }, [loc.pathname, loc.search, nav]);
  const ask = (next) => (dirtyRef.current ? setPending(() => next) : next());
  const dialog = pending && (
    <UnsavedDialog onContinue={() => setPending(null)} onDiscard={() => { const n = pending; dirtyRef.current = false; setPending(null); n(); }}
      onSave={async () => { const n = pending; const ok = await onSave(); setPending(null); if (ok) { dirtyRef.current = false; n(); } }} />
  );
  return { ask, dialog };
};

const UnsavedDialog = ({ onSave, onDiscard, onContinue }) => { useLang();
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4 backdrop-blur-sm" data-testid="unsaved-dialog">
      <div className="w-full max-w-md rounded-2xl border border-[var(--mv-border-2)] bg-[var(--mv-card)] p-6">
        <p className="text-base font-semibold">{tx("You have unsaved changes. Save before leaving?")}</p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onContinue} className="rounded-lg border border-[var(--mv-border-2)] px-4 py-2 text-xs" data-testid="unsaved-continue">{tx("Continue Editing")}</button>
          <button type="button" onClick={onDiscard} className="rounded-lg border border-red-400/40 px-4 py-2 text-xs text-red-300" data-testid="unsaved-discard">{tx("Discard Changes")}</button>
          <button type="button" onClick={onSave} className="mv-btn-primary rounded-lg px-4 py-2 text-xs" data-testid="unsaved-save">{tx("Save Changes")}</button>
        </div>
      </div>
    </div>
  );
};
